import { useEffect } from 'react';
import useAuthStore from '../../../store/rootStore.ts';
import { useGetAuthPayload } from '../authHooks.query.ts';
import { notify } from '@/feature/notifications';
import { useDialogStore } from '@/components/dialogs';
import { useConfig, useDisconnect } from 'wagmi';
import { captureError } from '../../../utils/sentryHelpers';
import { telemetry } from '../../../utils/telemetry';

export const usePrepareAuthPayload = () => {
  useConfig();
  const walletConnected = useAuthStore((state) => state.status === 'WALLET_CONNECTED');
  const address = useAuthStore((state) => state.address);
  const chainId = useAuthStore((state) => state.config?.chain.id);
  const authPayload = useAuthStore((state) => state.authPayload);
  const setAuthPayload = useAuthStore((state) => state.setAuthPayload);
  const { mutate: fetchAuthPayload, isPending, isError, reset } = useGetAuthPayload();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    reset();
  }, [address, reset]);

  // Fetch auth payload when wallet is connected
  useEffect(() => {
    const shouldFetchPayload =
      walletConnected && address != null && chainId != null && !isPending && !isError && authPayload == null;

    if (shouldFetchPayload) {
      fetchAuthPayload(
        {
          address: address,
          chain_id: chainId!,
        },
        {
          onSuccess: (data) => {
            setAuthPayload(data);
          },
          onError: (error: any) => {
            if (error.response?.status === 451) {
              useAuthStore.getState().blockByAuthority({ step: 'auth' });
              disconnect();
              useDialogStore.getState().openDialog('geoBlockDialog');
              return;
            }
            captureError(error, 'auth_payload_fetch');
            telemetry.logOnboarding('failed', {
              step: 'auth',
              reason: 'payload_fetch_error',
              status: error.response?.status ?? null,
            });
            notify('Something went wrong. Please try again or contact us on Discord.', 'error', {
              debugInfo: error instanceof Error ? error.message : 'Failed to get message to sign',
            });
          },
        }
      );
    }

    return () => {};
  }, [address, chainId, walletConnected, setAuthPayload, fetchAuthPayload, isPending, isError, authPayload, disconnect]);
};
